import { createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { createRoute } from "@tanstack/solid-router";
import { ArchiveRestore, Trash2 } from "@fleet/icons";

import type { AssetWithMeta } from "@tendly/shared";
import {
  deleteAssetMutation,
  hardDeleteAssetMutation,
  restoreAssetMutation,
  TRASH_PURGE_DAYS,
} from "@tendly/shared";
import { useTrashedAssetsQuery } from "@tendly/shared/solid";

import { Card, CardContent, PageContainer, PageHeader } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { AssetsIllustration } from "@/components/ui/illustrations";
import { useI18n } from "@/providers/i18n-provider";
import { useTendlyDeps } from "@/features/assets/hooks/use-tendly-deps";
import { assetKindIcon, assetKindLabels } from "@/features/assets/asset-display";
import { registerUndo } from "@/lib/undo";
import { appRoute } from "./route";

/**
 * Trash (UNDO-TRASH-LIGHT L2): assets with soft-delete (`deleted_at`).
 *
 * - Deleting an asset from the UI = moving it here. Its complete history
 *   (services/costs/readings/warranties/reminders) is PRESERVED and reappears
 *   intact on restore: soft-delete does not cascade. Usage regression/forecast
 *   recalculates itself because readings are never touched.
 * - "Restore" clears `deleted_at` (reversible: the pill offers re-delete).
 * - "Delete forever" performs the real DELETE (history cascades via
 *   FK) after explicit confirmation — irreversible, WITHOUT undo.
 * - Normal views exclude deleted rows via `deleted_at IS NULL`.
 */
function TrashPage() {
  const { t, locale } = useI18n();
  const deps = useTendlyDeps();

  const [confirmPurge, setConfirmPurge] = createSignal<{ id: string; name: string } | null>(null);
  const [submitting, setSubmitting] = createSignal(false);

  const trashedAssets = useTrashedAssetsQuery(deps);

  const rows = createMemo<AssetWithMeta[]>(() => trashedAssets.data ?? []);
  const isEmpty = createMemo(() => rows().length === 0);

  const undoMsg = (done: string) => ({
    done,
    undoLabel: t(msg`Undo`),
    undone: t(msg`Undone`),
    failed: t(msg`Couldn't undo`),
  });

  const MS_PER_DAY = 86_400_000;

  /** “deleted X ago” — relative time since `deleted_at` (trash checklist). */
  const deletedAgoLabel = (iso: string | null) => {
    if (!iso) {
      return "";
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    const rtf = new Intl.RelativeTimeFormat(locale(), { numeric: "auto" });
    const diffDays = Math.round((date.getTime() - Date.now()) / MS_PER_DAY);
    if (diffDays === 0) {
      const diffHours = Math.round((date.getTime() - Date.now()) / 3_600_000);
      if (diffHours === 0) {
        return rtf.format(Math.round((date.getTime() - Date.now()) / 60_000), "minute");
      }
      return rtf.format(diffHours, "hour");
    }
    return rtf.format(diffDays, "day");
  };

  /**
   * “will be permanently deleted in Y days” — countdown = deleted_at +
   * TRASH_PURGE_DAYS − now. Returns the remaining days (minimum 0) or null if
   * there is no valid date.
   */
  const daysUntilPurge = (iso: string | null): number | null => {
    if (!iso) {
      return null;
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    const purgeAt = date.getTime() + TRASH_PURGE_DAYS * MS_PER_DAY;
    return Math.max(0, Math.ceil((purgeAt - Date.now()) / MS_PER_DAY));
  };

  const restore = async (id: string) => {
    const db = deps.drizzle();
    if (!db || submitting()) {
      return;
    }
    setSubmitting(true);
    try {
      await restoreAssetMutation(db, id);
      // Undo a restore = return to the trash (soft-delete).
      registerUndo(undoMsg(t(msg`Asset restored`)), () => deleteAssetMutation(db, id));
    } finally {
      setSubmitting(false);
    }
  };

  const purge = async (id: string) => {
    const db = deps.drizzle();
    if (!db) {
      return;
    }
    await hardDeleteAssetMutation(db, id);
  };

  return (
    <PageContainer maxWidth="2xl" class="space-y-6">
      <div data-testid="trash-view">
        <PageHeader
          title={t(msg`Trash`)}
          description={t(msg`Deleted assets keep their whole history and can be restored anytime.`)}
          backTo="/profile"
          icon={Trash2}
        />
      </div>

      <Show
        when={!isEmpty()}
        fallback={
          <div data-testid="trash-empty">
            <EmptyState
              icon={Trash2}
              illustration={<AssetsIllustration />}
              title={t(msg`Trash is empty`)}
              description={t(msg`Assets you delete land here — nothing is lost right away.`)}
            />
          </div>
        }
      >
        <div class="flex flex-col gap-2">
          <For each={rows()}>
            {(asset) => {
              const Icon = assetKindIcon(asset.kind);
              const name = asset.name ?? "";
              return (
                <Card data-testid="trash-row" data-trash-name={name}>
                  <CardContent class="flex items-center gap-3 p-3">
                    <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <Icon class="h-5 w-5" />
                    </span>
                    <div class="min-w-0 flex-1">
                      <p class="truncate text-sm font-medium">{name}</p>
                      <p class="mt-0.5 text-xs text-muted-foreground">
                        {assetKindLabels(t)[asset.kind ?? "other"] ?? asset.kind} ·{" "}
                        {t(msg`Deleted {ago}`, { ago: deletedAgoLabel(asset.deleted_at) })}
                      </p>
                      <Show when={daysUntilPurge(asset.deleted_at) !== null}>
                        <p
                          class="text-xs text-muted-foreground/80"
                          data-testid="trash-purge-countdown"
                        >
                          {t(msg`Will be permanently deleted in {days} days`, {
                            days: daysUntilPurge(asset.deleted_at) ?? 0,
                          })}
                        </p>
                      </Show>
                    </div>
                    <div class="flex shrink-0 items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid="trash-restore"
                        disabled={!deps.drizzle() || submitting()}
                        onClick={() => void restore(asset.id)}
                      >
                        <ArchiveRestore class="mr-1 h-4 w-4" />
                        {t(msg`Restore`)}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        class="text-destructive hover:text-destructive"
                        data-testid="trash-delete-forever"
                        disabled={!deps.drizzle() || submitting()}
                        onClick={() => setConfirmPurge({ id: asset.id, name })}
                      >
                        <Trash2 class="mr-1 h-4 w-4" />
                        {t(msg`Delete forever`)}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            }}
          </For>
        </div>
      </Show>

      <ConfirmDialog
        open={confirmPurge() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmPurge(null);
          }
        }}
        title={t(msg`Delete forever?`)}
        description={t(
          msg`This permanently deletes it and its whole history. This cannot be undone.`,
        )}
        confirmLabel={t(msg`Delete forever`)}
        variant="destructive"
        onConfirm={() => {
          const target = confirmPurge();
          setConfirmPurge(null);
          if (target) {
            void purge(target.id);
          }
        }}
      />
    </PageContainer>
  );
}

export const profileTrashRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/profile/trash",
  component: TrashPage,
});
